import { compact, difference, map, uniq } from 'lodash';
import Controller from '../../shared/logic/Controller';
import Job from '../../shared/model/job/Job';
import { findByProp } from '../../shared/util/collection';
import Accounts from './Accounts';
import Contacts, { fields as contactsFields } from './Contacts';
import Filters from './Filters';
import Workflows from './Workflows';
import JobTypes from './JobTypes';
import Labels from './Labels';
import Tickets, { fields as ticketsFields } from './Tickets';

export const className = 'Jobs';
export const dependenciesRunInState = 'tasks';
export const fields = {
  list: [
    'id',
    'idWorkflow',
    'idTicket',
    'idContact',
    'idAssignedTo',
    'idOutlook',
    'idGoogle',
    'idLabels',
    'jobTemplate',
    'jobProgress',
    'absoluteDueDate',
    'relativeDueDate',
    'relativeInterval',
    'isDueDateAbsolute',
    'recurrence',
    'recurrenceInterval',
    'valueMonetary',
    'valueMonetaryCurrency',
    'valueTime',
    'valueTimeUnit',
    {
      repeat: [
        'repeats',
        'repeatEvery',
        'repeatOn',
        'repeatBy',
        { duration: ['occurrence', 'endOption', 'startDate', 'endDate'] }
      ]
    },
    'name',
    'description',
    'items',
    'isDueAhead',
    'isDue',
    'isOverdue',
    'isRecurring',
    'isCompleted',
    'isPinned',
    'orderByField'
  ]
};

export default class Jobs extends Controller {
  jobListHeaderSort = (order) => {
    const { orderBy, coerceTo, isDescending } = order;
    const query = { orderBy, coerceTo, isDescending: !isDescending };
    this.Storage.set('OrderBy', query);
    this.reset('available', { query });
  };

  jobsListHeaderComplete = () => {
    this.jobsCompleteConfirmationModalToggle(true);
    this.Scope.update('Selection.Jobs', this.getDataCollectionSelection());
  };

  jobsListHeaderInComplete = () => {
    this.jobsInCompleteConfirmationModalToggle(true);
    this.Scope.update('Selection.Jobs', this.getDataCollectionSelection());
  };

  jobsListHeaderDelete = () => {
    this.jobsDeleteConfirmationModalToggle(true);
    this.Scope.update('Selection.Jobs', this.getDataCollectionSelection());
  };

  jobsListLabel = (job) => {
    this.Scope.Event.dialogLabelSelect({
      event: null,
      labels: this.Scope.get('Data.Labels.Collection.Data'),
      selectedLabels: job.idLabels,
      data: job
    });
  };

  jobLabelsSelected = async (selectedLabels, job) => {
    await this.Data.update({ id: job.id, idLabels: selectedLabels });
    this.reset();
  };

  jobEdited = () => {
    this.reset();
  };

  jobsListEdit = (job) => {
    this.Scope.Event.jobEdit({
      event: null,
      job: job || null,
      isJob: true,
      showAccounts: true,
      showTicket: true,
      isTasksPage: true,
      stage: null,
      jobTypes: this.Scope.get('Data.JobTypes.Collection.Data'),
      accounts: this.Scope.get('Data.Accounts.Collection.Data'),
      workflow: null,
      ticket: null
    });
  };

  jobsConfirmationModalToggle = () => {
    this.Scope.update(
      'isVisible.ConfirmationModal',
      !this.Scope.get('isVisible.ConfirmationModal')
    );
  };

  jobsListConfirm = () => {
    const response = this.Scope.get('Resources.Confirmation.Response');
    if (response.name === 'complete' || response.name === 'incomplete') {
      this.jobsListToggleCompleteConfirm(response.value);
    }
    if (response.name === 'delete') {
      this.jobsListDeleteConfirm();
    }
  };

  jobsListCancel = () => {
    const response = this.Scope.get('Resources.Confirmation.Response');
    if (response.name === 'complete' || response.name === 'incomplete') {
      this.jobsListToggleCompleteCancel();
    }
    if (response.name === 'delete') {
      this.jobsListDeleteCancel();
    }
  };

  jobsCompleteConfirmationModalToggle = (isConfirm) => {
    this.Scope.set({
      Resources: {
        Confirmation: {
          Type: isConfirm ? 'error' : '',
          Icon: 'icon-alert-circle',
          Title: isConfirm ? 'Completing task(s)' : '',
          Text: isConfirm ? 'Are you sure you want to proceed?' : '',
          Response: {
            name: isConfirm ? 'complete' : '',
            value: isConfirm || false
          }
        }
      }
    });
    this.jobsConfirmationModalToggle();
  };

  jobsListComplete = (job) => {
    this.jobsCompleteConfirmationModalToggle(true);
    this.Scope.update('Selection.Jobs', [job.id]);
  };

  jobsInCompleteConfirmationModalToggle = (isConfirm) => {
    this.Scope.set({
      Resources: {
        Confirmation: {
          Type: isConfirm ? 'error' : '',
          Icon: 'icon-alert-circle',
          Title: isConfirm ? 'Un-completing task(s)' : '',
          Text: isConfirm ? 'Are you sure you want to proceed?' : '',
          Response: {
            name: isConfirm ? 'complete' : '',
            value: false
          }
        }
      }
    });
    this.jobsConfirmationModalToggle();
  };

  jobsListInComplete = (job) => {
    this.jobsInCompleteConfirmationModalToggle(true);
    this.Scope.update('Selection.Jobs', [job.id]);
  };

  jobsListToggleCompleteConfirm = async (value) => {
    const response = this.Scope.get('Resources.Confirmation.Response');
    if (response.name === 'complete') {
      this.jobsCompleteConfirmationModalToggle(false);
    }
    if (response.name === 'incomplete') {
      this.jobsInCompleteConfirmationModalToggle(false);
    }
    await this.Data.request('changeCompletedStatus', null, {
      idJobs: this.Scope.get('Selection.Jobs'),
      isCompleted: value
    });
    this.reset();
  };

  jobsListToggleCompleteCancel = () => {
    const response = this.Scope.get('Resources.Confirmation.Response');
    if (response.name === 'complete') {
      this.jobsCompleteConfirmationModalToggle(false);
    }
    if (response.name === 'incomplete') {
      this.jobsInCompleteConfirmationModalToggle(false);
    }
    this.Scope.update('Selection.Jobs', []);
  };

  jobsDeleteConfirmationModalToggle = (isConfirm) => {
    this.Scope.set({
      Resources: {
        Confirmation: {
          Type: isConfirm ? 'error' : '',
          Icon: 'icon-alert-circle',
          Title: isConfirm ? 'Deleting task(s)' : '',
          Text: isConfirm
            ? 'This will permanently erase all of the task(s) data. Data erasure is permanent and not recoverable. Are you sure you want to proceed?'
            : '',
          Response: {
            name: isConfirm ? 'delete' : '',
            value: isConfirm || false
          }
        }
      }
    });
    this.jobsConfirmationModalToggle();
  };

  jobsListDelete = (job) => {
    this.jobsDeleteConfirmationModalToggle(true);
    this.Scope.update('Selection.Jobs', [job.id]);
  };

  jobsListDeleteConfirm = async () => {
    this.jobsDeleteConfirmationModalToggle(false);
    await this.Data.request('jobsDelete', null, {
      idJobs: this.Scope.get('Selection.Jobs')
    });
    this.reset();
  };

  jobsListDeleteCancel = () => {
    this.jobsDeleteConfirmationModalToggle(false);
    this.Scope.update('Selection.Jobs', []);
  };

  jobsProgressView = () => {
    this.Scope.State.transitionTo('progress');
  };

  jobsFiltersToggle = () => {
    const isVisible = this.Scope.get('isVisible.Filters');
    if (!isVisible) {
      this.Scope.Event.intercomHide();
    } else {
      this.Scope.Event.intercomShow();
    }
    this.Scope.update('isVisible.Filters', !isVisible);
  };

  jobAdd = () => {
    this.jobsListEdit();
  };

  jobView = (job) => {
    if (job?.idContact) {
      const contact = findByProp(
        this.Scope.get('Data.Contacts')?.getCollectionData(),
        job?.idContact
      );
      if (contact) {
        this.Scope.State.transitionTo('contactView', {
          type: contact?.getEntity()?.toLowerCase(),
          id: contact?.id
        });
      }
    }
    if (job?.idTicket && job?.idWorkflow) {
      this.Scope.State.transitionTo('deal', {
        id: job?.idWorkflow,
        dd: job?.idTicket
      });
    }
  };

  jobsFiltersRemove = () => {
    this.Storage.remove('Filter');
    this.reset('available', { query: { idFilter: '', filterName: '' } });
  };

  jobsFiltersApply = (id, name) => {
    this.jobsFiltersToggle();
    const query = { idFilter: id, filterName: name };
    this.Storage.set('Filter', query);
    this.reset('available', { query });
  };

  jobsStartingLetter = (letter) => {
    const newLetter = letter !== 'ALL' ? letter : '';
    this.Storage.set('StartingWith', newLetter);
    this.reset('available', { query: { startingWith: newLetter } });
  };

  dataLoadersDynamic = async (jobs) => {
    if (jobs) {
      const ticketsCtrl = this.Scope.get('Data.Tickets');
      const contactsCtrl = this.Scope.get('Data.Contacts');
      const newIdTickets = uniq(map(jobs, 'idTicket'));
      const oldIdTickets = map(ticketsCtrl?.getCollectionData(), 'id');
      const newIdContacts = uniq(map(jobs, 'idContact'));
      const oldIdContacts = map(contactsCtrl?.getCollectionData(), 'id');
      await Promise.all([
        ticketsCtrl.load('specific', {
          query: {
            idTickets: compact(difference(newIdTickets, oldIdTickets)),
            fields: ticketsFields.names,
            orderBy: null,
            coerceTo: null
          }
        }),
        contactsCtrl.load('specific', {
          query: {
            idContacts: compact(difference(newIdContacts, oldIdContacts)),
            fields: contactsFields.names,
            orderBy: null,
            coerceTo: null
          }
        })
      ]).then(() => this.Scope.set({ isReady: { Tickets: true, Contacts: true } }, true));
    }
  };

  run = async (dependencies, dependenciesForAngular) => {
    const isInRunnableState = await this.init(
      [
        { name: 'collectionJobs:appended', handler: this.dataLoadersDynamic },

        { name: 'jobsListHeaderSort:click', handler: this.jobListHeaderSort },
        { name: 'jobsListHeaderComplete:click', handler: this.jobsListHeaderComplete },
        { name: 'jobsListHeaderInComplete:click', handler: this.jobsListHeaderComplete },
        { name: 'jobsListHeaderDelete:click', handler: this.jobsListHeaderDelete },

        { name: 'jobsListLabel:click', handler: this.jobsListLabel },
        { name: 'jobsListEdit:click', handler: this.jobsListEdit },
        { name: 'jobsListComplete:click', handler: this.jobsListComplete },
        { name: 'jobsListInComplete:click', handler: this.jobsListInComplete },
        { name: 'jobsListDelete:click', handler: this.jobsListDelete },

        { name: 'jobsListConfirm:click', handler: this.jobsListConfirm },
        { name: 'jobsListCancel:click', handler: this.jobsListCancel },

        { name: 'jobsProgressView:click', handler: this.jobsProgressView },
        { name: 'jobsFiltersToggle:click', handler: this.jobsFiltersToggle },
        { name: 'jobAddNew:click', handler: this.jobAdd },

        { name: 'itemListClickItem:click', handler: this.jobView },
        { name: 'itemListSelectItem', handler: undefined },

        { name: 'removeFilter:click', handler: this.jobsFiltersRemove },
        { name: 'startingLetter:click', handler: this.jobsStartingLetter }
      ],
      dependencies,
      dependenciesForAngular,
      dependenciesRunInState
    );
    if (!isInRunnableState) {
      return;
    }

    this.Scope.WebPage.pageTitle('Tasks');
    await Promise.all([
      this.Scope.get('Data.Accounts')?.load('available'),
      this.Scope.get('Data.Filters')?.load('available', { query: { type: 'job' } }),
      this.Scope.get('Data.JobTypes')?.load('available'),
      this.Scope.get('Data.Labels')?.load('available', { query: { type: 'job' } }),
      this.Scope.get('Data.Workflows')?.load('available')
    ]).then(() =>
      this.Scope.set(
        {
          isReady: {
            Jobs: true,
            Accounts: true,
            Filters: true,
            JobTypes: true,
            Labels: true,
            Workflows: true
          },
          jobLabelsSelected: this.jobLabelsSelected,
          jobEdited: this.jobEdited,
          jobListApplyFilter: this.jobsFiltersApply,
          jobListToggleFilters: this.jobsFiltersToggle
        },
        true
      )
    );
  };

  constructor(
    $rootScope,
    $scope,
    $state,
    $timeout,
    webpage,
    eventService,
    authenticationService
  ) {
    'ngInject';

    const angularDependencies = [
      $rootScope,
      $scope,
      $state,
      $timeout,
      webpage,
      eventService,
      authenticationService
    ];
    super(angularDependencies, {
      Storage: {
        Table: className
      },
      Scope: {
        Defaults: {
          Name: className,
          Resources: {
            Confirmation: {
              Width: '50',
              Icon: '',
              Type: '',
              Title: '',
              Text: '',
              Response: {
                name: '',
                value: false
              }
            }
          },
          Selection: { Jobs: [] },
          isVisible: {
            Filters: false,
            ConfirmationModal: false
          }
        }
      },
      Data: {
        Model: Job,
        ModelName: className,
        Requests: {
          available: {
            url: '/api/v1/jobs/available/',
            query: {
              orderBy: 'absoluteDueDate',
              coerceTo: 'number',
              isDescending: true
            }
          },
          crud: {
            url: '/api/v1/job/',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            }
          },
          jobsDelete: {
            url: '/api/v1/jobs/delete/',
            method: 'POST',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            }
          },
          changeCompletedStatus: {
            url: '/api/v1/jobs/change-completed-status/',
            method: 'POST',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            }
          }
        }
      }
    });

    this.reset = (request, requestParameters, scope, isUpdated) => {
      this.resetDataAndScope(
        request || 'available',
        requestParameters || {},
        scope || {
          Data: { Jobs: this.Data }
        },
        isUpdated || true
      );
    };

    this.run(
      [Accounts, Contacts, Filters, JobTypes, Labels, Tickets, Workflows],
      angularDependencies
    );
  }
}
